import { FormikProvider, useFormik } from 'formik';
import { object, string } from 'yup';
import React, { useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import styled from 'styled-components';
import { InputLabel, List, ListItem, FormControl } from '@mui/material';
import type {
  ApplicantInformation,
  RecommendationLevel,
  TickCellHeader,
} from './types';
import { ratingHeaderCells, selectOptions } from './options';
import { PAGE_TEXT } from './lang';
import { REFEREE_FORM_STATUS } from './constants';
import { PROGRAMME_LEVEL_PAGE_TEXT } from '../lang';
import {
  type RefereeFormFormData,
  type SelectionQuestion,
  type UpdateRefFormInput,
  useSubmitRefereeForm,
  useQueryRefereeReportStatus,
  useQueryRefereeFormInfo,
  useGetAcademicRefereeToken,
  useGetChoiceData,
  type RefereeChecks,
} from '../../../graphql';
import {
  Accordion,
  Alert,
  Button,
  Select,
  TextField,
  Table,
  type AlertDetails,
  COMMON_PAGE_TEXT,
  Radio,
  Loading,
} from '../../../../../components';

export type RefereeFormProps = {
  initialValues?: RefereeFormFormData;
  enableEdit?: boolean;
};

export const RefereeForm: React.FunctionComponent = (
  props: RefereeFormProps,
) => {
  const { initialValues: init, enableEdit: initEnable } = props;
  const enableEdit = initEnable ?? true;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  // get the token from the url, and use it to get the application information
  const token = new URLSearchParams(window.location.search).get('token');
  if (token === null) {
    window.location.replace('/error');
    return <div />;
  }

  const {
    data: decodedToken,
    loading: tokenLoading,
    error: decodeError,
  } = useGetAcademicRefereeToken(token);

  // keeping decodedToken?.formId/decodedToken?.applicationId/decodedToken?.refereeId for compatibility
  const formId = decodedToken?.userInfo?.formId ?? decodedToken?.formId ?? '';
  const applicationId =
    decodedToken?.userInfo?.applicationId ?? decodedToken?.applicationId ?? '';
  const refereeId =
    decodedToken?.userInfo?.refereeId ??
    decodedToken?.refereeId ??
    decodedToken?.userInfo?.academicRefereeId ?? // temp solution for compatibility.
    '';

  const {
    status,
    loading: refereeReportStatusLoading,
    error: refereeReportStatusError,
  } = useQueryRefereeReportStatus(formId);

  if (status === REFEREE_FORM_STATUS.EXPIRED) {
    window.location.replace('/referee-report/expired');
  } else if (status === REFEREE_FORM_STATUS.SUBMITTED) {
    window.location.replace('/referee-report/successful');
  }

  const {
    data: applicantInformation,
    error: applicantInformationErr,
    loading: applicantInformationLoading,
  } = useQueryRefereeFormInfo({
    variables: {
      formId,
    },
    skip: decodedToken == null || !formId,
  });

  const {
    programmeChoiceMetadata,
    error: metadataErr,
    loading: metadataLoading,
  } = useGetChoiceData();

  const programmeLevelMapping: Record<string, string> =
    PROGRAMME_LEVEL_PAGE_TEXT;

  const { submitRefereeForm } = useSubmitRefereeForm();

  const initialValues = {
    check: {
      intellectualCapacity: '',
      analyticalPower: '',
      knowledgeOfStudy: '',
      judgment: '',
      imagination: '',
      motivation: '',
      skillsOfWriting: '',
      conductingAbility: '',
      independentWork: '',
      reliability: '',
    },
    capacity: '',
    group: '',
    ability: '',
    recommendation: '',
    ...init,
  };

  const handleSubmit = async (values: RefereeFormFormData): Promise<void> => {
    const formQuestions = [
      {
        number: 1,
        question: PAGE_TEXT.question[0],
        answer: values.capacity,
      },
      {
        number: 3,
        question: PAGE_TEXT.question[2],
        answer: values.group,
      },
      {
        number: 4,
        question: PAGE_TEXT.question[3],
        answer: values.ability,
      },
    ];

    const checks = Object.keys(values.check);

    const selectionQuestions = checks.map((abbr, index) => {
      const question = PAGE_TEXT.labels[index].label;
      const option = values.check?.[abbr as keyof RefereeChecks];
      return {
        number: index,
        question,
        option,
      } satisfies SelectionQuestion;
    });

    const input: UpdateRefFormInput | null =
      decodedToken !== undefined
        ? {
            applicationId,
            academicRefereeId: refereeId,
            formId,
            formQuestions,
            selectionQuestions,
            recommendation: values.recommendation as RecommendationLevel,
          }
        : null;

    const res =
      input != null
        ? await submitRefereeForm({
            variables: {
              input,
            },
          })
        : null;

    if (res?.data?.updateRefForm === formId) {
      window.location.replace('/referee-report/successful');
    } else {
      setOpenAlert(true);
      setAlertDetails({
        alertContent: 'Error submitting the form.',
        alertType: 'error',
      });
    }
  };

  const formik = useFormik<RefereeFormFormData>({
    initialValues,
    validationSchema: object({
      capacity: string().required('Required'),
      group: string().required('Required'),
      ability: string().required('Required'),
      recommendation: string().required('Required'),
      check: object({
        intellectualCapacity: string().required('Required'),
        analyticalPower: string().required('Required'),
        knowledgeOfStudy: string().required('Required'),
        judgment: string().required('Required'),
        imagination: string().required('Required'),
        motivation: string().required('Required'),
        skillsOfWriting: string().required('Required'),
        conductingAbility: string().required('Required'),
        independentWork: string().required('Required'),
        reliability: string().required('Required'),
      }),
    }),
    onSubmit: handleSubmit,
  });

  const handleSelection = (): any => {
    return PAGE_TEXT.labels.map(label => {
      const radio = Object.keys(PAGE_TEXT.level).map(value => {
        const key = `${label.abbr}.${value}`;
        const name = `check.${label.abbr}`;

        return (
          <FormControl key={key}>
            <Radio
              label=""
              size="small"
              key={`${label.label}.${value}`}
              value={value}
              name={name}
              error={Boolean(
                formik.errors?.check?.[label.abbr as keyof RefereeChecks],
              )}
              checked={
                formik.values.check[label.abbr as keyof RefereeChecks] === value
              }
              onChange={formik.handleChange}
            />
          </FormControl>
        );
      });
      return {
        title: label.label,
        EXCELLENT: radio[0],
        VERY_GOOD: radio[1],
        SATISFACTORY: radio[2],
        AVERAGE_OR_BELOW: radio[3],
        NO_BASIS_FOR_JUDGMENT: radio[4],
      };
    });
  };

  const handleSelectionDisplay = useMemo((): any => {
    return (
      <>
        <InputLabel sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {PAGE_TEXT.question[1]}
        </InputLabel>
        <div className="p-4" id="check">
          <Table<TickCellHeader>
            headCells={ratingHeaderCells}
            data={handleSelection()}
            fontSize={14}
            handleRequestSort={() => {}}
          />
        </div>
      </>
    );
  }, [formik.values.check]);

  const handleInputForm = (inputField: {
    name: string;
    label: string;
    maxLength: number;
  }): any => {
    const { name } = inputField;
    return (
      <>
        <InputLabel sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {inputField.label}
        </InputLabel>
        <div className="w-full p-4">
          <TextField
            key={name}
            name={name}
            id={name}
            disabled={!enableEdit}
            value={formik.values[name]}
            onChange={formik.handleChange}
            multiline
            minRows={10}
            maxRows={25}
            fullWidth
            onBlur={formik.handleBlur}
            error={
              Boolean(formik.touched[name]) && Boolean(formik.errors[name])
            }
            helperText={
              Boolean(formik.touched[name]) && (formik.errors[name] as string)
            }
          />
        </div>
      </>
    );
  };

  const displayInformation = (name: string, value: string): React.ReactNode => {
    return (
      <div className="grid grid-cols-2 h-8" key={`${name}-${value}`}>
        <div className="ml-8">{PAGE_TEXT.information[name]}</div>
        <div>{value}</div>
      </div>
    );
  };

  const basicInformationDisplay = (): React.ReactNode => {
    const info: ApplicantInformation = {
      ...applicantInformation?.getRefereeFormInfo,
    };

    info.department =
      programmeChoiceMetadata.departments.find(e => e.value === info.department)
        ?.label ?? '';

    info.programme = programmeLevelMapping[info.programme];

    const fields = [
      'nameOfApplicant',
      'department',
      'programme',
      'researchArea',
    ];

    if (applicantInformation?.getRefereeFormInfo != null)
      return (
        <div className="pt-2">
          {fields.map(key => {
            return displayInformation(key, info[key].toString());
          })}
        </div>
      );
    else return <div className="pt-2">Loading applicant information</div>;
  };

  const handleRefereeFromSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    formik.handleSubmit();
    const isValid = await formik.validateForm();
    if (Object.keys(isValid).length > 0) {
      requestAnimationFrame(() => {
        // Use a second requestAnimationFrame to ensure the scroll happens after the DOM updates
        requestAnimationFrame(() => {
          const firstErrorField = Object.keys(isValid)[0];
          const errorElement = document.getElementById(firstErrorField);

          if (errorElement !== null) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        });
      });
    }
  };

  if (
    refereeReportStatusError ??
    decodeError ??
    applicantInformationErr ??
    metadataErr
  ) {
    return (
      <Alert
        openAlert
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            (refereeReportStatusError as Error)?.message +
            (decodeError as Error)?.message +
            (applicantInformationErr as Error)?.message +
            (metadataErr as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }
  const loading =
    refereeReportStatusLoading ||
    metadataLoading ||
    applicantInformationLoading ||
    tokenLoading;

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <RefereeFormContainer>
          <h1 className="w-full flex justify-center p-4">
            Academic Referee&apos;s Report
          </h1>
          {/* Disclaimers */}
          <SectionBlock>
            <Accordion
              defaultExpanded
              title={'Notes to Referees'}
              details={
                <div>
                  <List sx={{ listStyleType: 'disc', fontSize: '1.05rem' }}>
                    {PAGE_TEXT.notesToReferees.map((note: string, index) => {
                      return (
                        <ListItem
                          key={index}
                          className="ml-8 mr-2 "
                          sx={{ display: 'list-item' }}
                        >
                          {note}
                        </ListItem>
                      );
                    })}
                  </List>
                </div>
              }
            />
          </SectionBlock>
          {/*     Basic Information Area */}
          <SectionBlock>
            <Accordion
              title={'Application Information'}
              defaultExpanded
              details={basicInformationDisplay()}
            />
          </SectionBlock>
          {/*     Form Area */}
          <SectionBlock>
            <FormikProvider value={formik}>
              <form onSubmit={handleRefereeFromSubmit}>
                <Grid>
                  {handleInputForm({
                    name: 'capacity',
                    label: PAGE_TEXT.question[0],
                    maxLength: 1000,
                  })}
                </Grid>
                <Grid>{handleSelectionDisplay}</Grid>
                <Grid>
                  {handleInputForm({
                    name: 'group',
                    label: PAGE_TEXT.question[2],
                    maxLength: 1000,
                  })}
                </Grid>
                <Grid>
                  {handleInputForm({
                    name: 'ability',
                    label: PAGE_TEXT.question[3],
                    maxLength: 1000,
                  })}
                </Grid>
                <Grid>
                  <InputLabel
                    sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
                  >
                    {PAGE_TEXT.question[4]}
                  </InputLabel>
                  <div className="w-full p-4">
                    <Select
                      options={selectOptions}
                      fullWidth={true}
                      name={'recommendation'}
                      id={'recommendation'}
                      value={formik.values.recommendation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.recommendation &&
                        Boolean(formik.errors.recommendation)
                      }
                      helperText={
                        formik.touched.recommendation &&
                        formik.errors.recommendation
                      }
                    />
                  </div>
                </Grid>

                <div className="w-full flex justify-center p-4">
                  <Button type="submit" variant="contained">
                    {COMMON_PAGE_TEXT.BUTTON.SUBMIT}
                  </Button>
                </div>
              </form>
            </FormikProvider>
          </SectionBlock>
          <Alert
            openAlert={openAlert}
            setOpenAlert={setOpenAlert}
            alertDetails={alertDetails}
          />
        </RefereeFormContainer>
      )}
    </>
  );
};

const RefereeFormContainer = styled.div`
  width: 80%;
  margin: 12px auto 48px;

  h1 {
    font-size: 2rem;
    margin-right: 1rem;
  }
`;

const SectionBlock = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;

  .MuiGrid-root {
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;
